/* Heirloom Tenant Home — album-page, archival, hand-pressed.

   Emotional intent: PRESERVATION. Reads like a hardbound family album.
   Two side-by-side portraits at the top (an "album spread"), each with a
   hand-typeset caption underneath. Drop-cap on the "Foreword" body.
   Services rendered as numbered "chapters" (I, II, III) with serif Roman
   numerals. Subtle paper-cream backdrop. Caramel hairlines everywhere.

   Structurally distinct: NO full-bleed photo, paired-portrait hero,
   chapter-numbered services, drop-cap foreword, page numbers at the
   bottom of each section. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const FALLBACK = "https://images.unsplash.com/photo-1502635385003-ee1e6a1a742d?auto=format&fit=crop&w=1400&q=80";
const ROMAN = ["I", "II", "III", "IV", "V"];

const PageNo = ({ n }) => (
  <div className="text-center text-[10px] uppercase tracking-[0.42em] py-6"
       style={{ color: "var(--tenant-fg-mute)" }}>
    — {n} —
  </div>
);

export default function HeirloomTenantHome() {
  const { settings, loading } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const port = settings?.portfolio_images || [];
  const heroLeft = settings?.hero_image || port[0] || FALLBACK;
  // Heirloom is the only template that surfaces a second hero ("album
  // spread"). When the studio hasn't set hero_image_secondary explicitly
  // we fall back to portfolio_images[1] so the spread still looks
  // intentional out of the box.
  const heroRight = settings?.hero_image_secondary || port[1] || port[0] || FALLBACK;
  const headline = settings?.hero_title_line1 || "Volume I.";
  const subhead = settings?.hero_title_line2 || "Of a life beautifully lived.";
  const intro = settings?.hero_description || `${studioName} crafts heirloom portraits — printed, bound, and built to outlive the phones they were never made for.`;
  const aboutTitle = settings?.about_title || "Foreword.";
  const aboutBody = settings?.about_body || `For three generations, families have come to us when the moment was big enough to keep. Newborns, milestones, the in-between days. We photograph slowly, print carefully, and hand you something you'll still hold in twenty years.`;

  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(Array.isArray(r.data) ? r.data.slice(0, 4) : [])).catch(() => {});
  }, []);
  useEffect(() => { document.title = `${studioName} — Heirloom Portrait Studio`; }, [studioName]);

  if (loading && !settings) return <EditorialSkeleton eyebrow="Opening the album." />;

  // Drop-cap first letter
  const firstChar = aboutBody.charAt(0);
  const restBody = aboutBody.slice(1);

  return (
    <div data-testid="heirloom-tenant-home"
      style={{ background: "var(--tenant-bg, #F8F2EA)", color: "var(--tenant-fg, #2E2419)" }}>

      {/* ── HERO — TWO-PORTRAIT ALBUM SPREAD ──────────────────────────── */}
      <section className="px-6 lg:px-16 pt-16 pb-12" data-testid="heirloom-hero">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
            {[
              { src: heroLeft, caption: "Portrait · Sitting №01" },
              { src: heroRight, caption: "Detail · Sitting №01" },
            ].map((f, i) => (
              <figure key={i}>
                <div className="aspect-[3/4] overflow-hidden border-8"
                     style={{ borderColor: "color-mix(in srgb, var(--tenant-bg, #F8F2EA) 80%, var(--tenant-fg, #2E2419) 20%)" }}>
                  <img src={optimizeImageUrl(f.src, { w: 1100 })} alt=""
                    className="w-full h-full object-cover"
                    loading={i === 0 ? "eager" : "lazy"} decoding="async" />
                </div>
                <figcaption className="text-center text-[10px] uppercase tracking-[0.32em] mt-3 italic"
                            style={{ color: "var(--tenant-fg-soft)", fontFamily: "var(--tenant-serif)" }}>
                  {f.caption}
                </figcaption>
              </figure>
            ))}
          </div>

          {/* Title page */}
          <div className="text-center mt-20 mb-6 max-w-2xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.5em] mb-6" style={{ color: "var(--tenant-accent)" }}>
              The {studioName} Archive
            </div>
            <h1 className="italic leading-[1.05]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
              {headline}
              <br /><span style={{ color: "var(--tenant-accent)" }}>{subhead}</span>
            </h1>
            <p className="mt-8 max-w-md mx-auto leading-relaxed text-sm" style={{ color: "var(--tenant-fg-soft)" }}>
              {intro}
            </p>
            <div className="mt-10">
              <Link to={tenantHref("/booking")} data-testid="heirloom-hero-book"
                className="inline-flex items-center gap-3 px-8 py-3.5 text-[10px] uppercase tracking-[0.42em] border-2 transition-colors"
                style={{ borderColor: "var(--tenant-fg)", color: "var(--tenant-fg)" }}>
                Begin Volume II →
              </Link>
            </div>
          </div>
        </div>
        <PageNo n="01" />
      </section>

      {/* ── FOREWORD with drop-cap ───────────────────────────────────── */}
      <section className="px-6 lg:px-16 py-16 border-t border-b"
               style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", borderStyle: "dotted" }}>
        <div className="max-w-2xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.42em] mb-3 text-center" style={{ color: "var(--tenant-accent)" }}>
            Foreword
          </div>
          <h2 className="italic text-center mb-10" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
            {aboutTitle}
          </h2>
          <p className="leading-[1.85] text-base" style={{ fontFamily: "var(--tenant-serif)" }}>
            <span className="float-left text-[5rem] leading-[0.85] mr-3 mt-1 italic"
                  style={{ color: "var(--tenant-accent)", fontFamily: "var(--tenant-serif)" }}>
              {firstChar}
            </span>
            {restBody}
          </p>
        </div>
        <PageNo n="02" />
      </section>

      {/* ── CHAPTERS (services) ──────────────────────────────────────── */}
      {services.length > 0 && (
        <section className="px-6 lg:px-16 py-16" data-testid="heirloom-services">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <div className="text-[10px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>
                Chapters
              </div>
              <h2 className="italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
                Sessions, by occasion.
              </h2>
            </div>
            <ol className="space-y-10">
              {services.map((svc, i) => (
                <li key={svc.id} className="grid grid-cols-[60px_1fr] gap-6 items-start">
                  <div className="text-4xl italic text-right" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>
                    {ROMAN[i] || (i + 1)}
                  </div>
                  <div className="pb-8 border-b" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", borderStyle: "dotted" }}>
                    <div className="italic text-2xl mb-2" style={{ fontFamily: "var(--tenant-serif)" }}>{svc.name}</div>
                    {svc.short_description && (
                      <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--tenant-fg-soft)" }}>{svc.short_description}</p>
                    )}
                    <Link to={tenantHref(`/services#${svc.id || svc.slug || ""}`)}
                      className="text-[10px] uppercase tracking-[0.32em] inline-flex items-center gap-2"
                      style={{ color: "var(--tenant-accent)" }}>
                      Read this chapter →
                    </Link>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <PageNo n="03" />
        </section>
      )}

      {/* ── PORTFOLIO — 4-image album page grid with captions ────────── */}
      {port.length > 0 && (
        <section className="px-6 lg:px-16 py-16 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", borderStyle: "dotted" }}>
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <div className="text-[10px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>Selected plates</div>
              <h2 className="italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.5rem, 2.5vw, 2.25rem)" }}>From the archive.</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {port.slice(0, 4).map((src, i) => (
                <figure key={i}>
                  <div className="aspect-[3/4] overflow-hidden border-4"
                       style={{ borderColor: "color-mix(in srgb, var(--tenant-bg, #F8F2EA) 80%, var(--tenant-fg, #2E2419) 20%)" }}>
                    <img src={optimizeImageUrl(src, { w: 800 })} alt=""
                      className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </div>
                  <figcaption className="text-center text-[9px] uppercase tracking-[0.32em] mt-2 italic"
                              style={{ color: "var(--tenant-fg-soft)", fontFamily: "var(--tenant-serif)" }}>
                    Plate №{String(i + 1).padStart(2, "0")}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
          <PageNo n="04" />
        </section>
      )}
    </div>
  );
}
